import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Check, Minus, Plus, Trash2 } from "lucide-react";
import { colors, type, spacing, radius, formatCents } from "@/theme";
import { useCustomer } from "@/context/CustomerContext";

const DEMO_MAX_ITEMS = 6;

export default function CustomerCart() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { cart, updateQty, cartTotal, cartCount, sendOrder } = useCustomer();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const isDemo = slug === "demo";

  const onSend = async () => {
    if (isDemo && cartCount > DEMO_MAX_ITEMS) { setError(`Nel menu di esempio puoi ordinare al massimo ${DEMO_MAX_ITEMS} articoli.`); return; }
    setBusy(true); setError(null);
    try {
      const res = await sendOrder();
      if (isDemo) { navigate(`/m/${slug}/cucina?o=${res.order_id}`, { replace: true }); return; }
      setSent(true);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <div style={{ minHeight: "100vh", background: colors.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: spacing.lg, gap: spacing.md, textAlign: "center" }}>
        <div style={{ width: 88, height: 88, borderRadius: 44, background: colors.success, display: "flex", alignItems: "center", justifyContent: "center" }}><Check size={44} color={colors.onAccent} /></div>
        <div style={{ ...type.serif, fontSize: 28, color: colors.ink }}>Ordine ricevuto</div>
        <div style={{ ...type.sans, fontSize: 16, color: colors.inkSoft, lineHeight: "24px" }}>Lo abbiamo mandato in cucina.<br />Lo trovi nel riepilogo del conto.</div>
        <button data-testid="back-to-menu" onClick={() => navigate(`/m/${slug}/menu`, { replace: true })} style={{ ...cta, marginTop: spacing.md, padding: `0 ${spacing.xl}px` }}>Continua a ordinare</button>
        <button data-testid="go-conto" onClick={() => navigate(`/m/${slug}/conto`, { replace: true })} style={{ background: "none", border: "none", padding: spacing.sm, ...type.sansMedium, fontSize: 15, color: colors.inkSoft }}>Vedi il conto</button>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: colors.bg }}>
      <div style={{ maxWidth: 480, margin: "0 auto", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: `${spacing.md}px ${spacing.lg}px` }}>
          <button data-testid="back-button" onClick={() => navigate(-1)} style={{ background: "none", border: "none", display: "flex", padding: 0 }}><ChevronLeft size={26} color={colors.ink} /></button>
          <div style={{ ...type.serif, fontSize: 22, color: colors.ink }}>Il tuo carrello</div>
          <div style={{ width: 26 }} />
        </div>

        {cart.length === 0 ? (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: spacing.md, padding: spacing.lg }}>
            <div style={{ ...type.serif, fontSize: 20, color: colors.ink }}>Il carrello è vuoto</div>
            <button data-testid="empty-back" onClick={() => navigate(-1)} style={{ ...cta, padding: `0 ${spacing.xl}px` }}>Torna al menu</button>
          </div>
        ) : (
          <>
            <div style={{ padding: spacing.lg, display: "flex", flexDirection: "column", gap: spacing.md, paddingBottom: 140 }}>
              {cart.map((l) => (
                <div key={l.key} data-testid={`cart-line-${l.key}`} style={{ display: "flex", alignItems: "center", background: colors.surface, borderRadius: radius.lg, padding: spacing.md, border: `1px solid ${colors.line}` }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ ...type.serif, fontSize: 17, color: colors.ink }}>{l.nome}</div>
                    <div style={{ ...type.sansMedium, fontSize: 14, color: colors.inkSoft, marginTop: 2 }}>{formatCents(l.prezzo_cent)}</div>
                    {(l.note_rapide.length > 0 || l.nota_riga) ? <div style={{ ...type.sans, fontSize: 12, color: colors.accent, marginTop: 4 }}>{[...l.note_rapide, l.nota_riga].filter(Boolean).join(" · ")}</div> : null}
                  </div>
                  <div style={stepper}>
                    <button data-testid={`minus-${l.key}`} onClick={() => updateQty(l.key, l.quantita - 1)} style={stepBtn}>{l.quantita === 1 ? <Trash2 size={16} color={colors.danger} /> : <Minus size={16} color={colors.ink} />}</button>
                    <span style={{ ...type.sansSemi, fontSize: 16, color: colors.ink, minWidth: 22, textAlign: "center" }}>{l.quantita}</span>
                    <button data-testid={`plus-${l.key}`} onClick={() => updateQty(l.key, l.quantita + 1)} style={stepBtn}><Plus size={16} color={colors.ink} /></button>
                  </div>
                </div>
              ))}
              {error ? <div style={{ ...type.sansMedium, fontSize: 14, color: colors.danger, textAlign: "center" }}>{error}</div> : null}
            </div>

            <div style={{ position: "sticky", bottom: 0, padding: spacing.lg, background: colors.bg, borderTop: `1px solid ${colors.line}`, maxWidth: 480, margin: "auto 0 0" }}>
              <button data-testid="send-order-button" onClick={onSend} disabled={busy} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", background: colors.accent, height: 56, border: "none", borderRadius: radius.md, padding: `0 ${spacing.lg}px` }}>
                <span style={{ color: colors.onAccent, ...type.sansSemi, fontSize: 17 }}>{busy ? "Invio…" : "Invia in cucina"}</span>
                {!busy ? <span style={{ color: colors.onAccent, ...type.sansSemi, fontSize: 17 }}>{formatCents(cartTotal)}</span> : null}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const cta = { background: colors.accent, height: 52, border: "none", borderRadius: radius.md, color: colors.onAccent, ...type.sansSemi, fontSize: 16 };
const stepper = { display: "flex", alignItems: "center", background: colors.bg, borderRadius: radius.md, border: `1px solid ${colors.line}` };
const stepBtn = { width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", background: "none", border: "none" };
